import { convertToMI, MILES_TO_KPH } from 'Core/data/Helpers';

const FEET_PER_METRE = 3.28084;
const GALLONS_PER_LITRE = 0.264172;
/** mpg = 235.215 / (L/100 km). */
const MPG_PER_L100KM = 235.214583;
const SECONDS_PER_HOUR = 3600;

const identity = (value) => value;
const rule = (key, convert, maximumFractionDigits) => ({ key, convert, maximumFractionDigits });

const secondsToHours = rule('hours', (s) => s / SECONDS_PER_HOUR, 1);

/**
 * Whatever the user's preference: engine time in hours, percentages with %.
 * CerebrumX serves seconds as 's' (e.g. cx_total_engine_time on staging);
 * z1-ui-main's sample used 'sec' — both are accepted.
 */
const ALWAYS_RULES = {
	perc: rule('percent', identity, 1),
	sec: secondsToHours,
	s: secondsToHours,
};

/** `isKph === false`: z1-ui-main's forced-imperial rules. */
const IMPERIAL_RULES = {
	'km/h': rule('mph', convertToMI, 0),
	km: rule('miles', convertToMI, 1),
	m: rule('feet', (m) => m * FEET_PER_METRE, 0),
	'°c': rule('fahrenheit', (c) => c * 1.8 + 32, 0),
	l: rule('gallons', (l) => l * GALLONS_PER_LITRE, 1),
	'l/100km': rule('mpg', (l) => MPG_PER_L100KM / l, 1),
};

/** `isKph === true`: metric units as served; imperial ones converted to metric. */
const METRIC_RULES = {
	'km/h': rule('kph', identity, 0),
	km: rule('km', identity, 1),
	m: rule('meters', identity, 0),
	'°c': rule('celsius', identity, 0),
	l: rule('liters', identity, 1),
	'l/100km': rule('l100km', identity, 1),
	mph: rule('kph', (mph) => mph * MILES_TO_KPH, 0),
	mi: rule('km', (mi) => mi * MILES_TO_KPH, 1),
};

const unitRule = (unit, isKph) => {
	if (unit === undefined) return undefined;
	const key = unit.toLowerCase();
	const systemRules = isKph ? METRIC_RULES : IMPERIAL_RULES;
	return ALWAYS_RULES[key] ?? systemRules[key];
};

export const formatNumber = (value, locale, maximumFractionDigits = 1) =>
	new Intl.NumberFormat(locale, { maximumFractionDigits }).format(value);

/**
 * A measure in display form for the user's unit system: converted when its
 * unit has a rule (a non-positive consumption cannot become mpg and stays as
 * served), shown as served with any other unit, a bare number when unitless.
 */
export const formatMeasure = (measure, { t, locale, isKph }) => {
	if (measure === undefined) return undefined;
	const { value, unit } = measure;
	const found = unitRule(unit, isKph);
	if (found !== undefined && !(found.key === 'mpg' && value <= 0)) {
		return t(`oemDataModal_${found.key}`, {
			value: formatNumber(found.convert(value), locale, found.maximumFractionDigits),
		});
	}
	const amount = formatNumber(value, locale, 2);
	if (unit === undefined) return amount;
	return t('oemDataModal_measure', { value: amount, unit });
};

export const formatPercent = (value, { t, locale }) => {
	if (value === undefined) return undefined;
	return t('oemDataModal_percent', { value: formatNumber(value, locale) });
};

/** "35.791805, -78.844376" when both coordinates are present. */
export const formatCoordinates = (latitude, longitude) => {
	if (latitude === undefined || longitude === undefined) return undefined;
	return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
};

/** "2025 TESLA Model 3 Base" from whichever identity parts the payload carries. */
export const vehicleLabel = (view) => {
	const parts = [view.year, view.make, view.model, view.trim].filter((part) => part !== undefined);
	if (parts.length === 0) return undefined;
	return parts.join(' ');
};
